'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { PrudsysCategoryToProductsDocument } from '@packages/gql/generated/shopping/PrudsysCategoryToProductsDocument';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCardV2.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type PrudsysCategoryToProductsRecoSliderProps = Pick<RequestParams, 'categoryId'> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query PrudsysCategoryToProducts($locale: String!, $categoryId: String!) {
    prudsysCategoryToProducts(locale: $locale, categoryId: $categoryId) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
      extendedViewUrl
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysCategoryToProducts';

/**
 * PrudsysCategoryToProductsProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysCategoryToProductsRecoSlider: FC<PrudsysCategoryToProductsRecoSliderProps> = ({
  backgroundColor,
  categoryId,
  elementIndex = 0,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  preferTitle = false,
  title,
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysCategoryToProductsDocument,
    variables: {
      categoryId,
      locale,
    },
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-locale': locale } } }),
      [locale],
    ),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysCategoryToProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.prudsysCategoryToProducts.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysCategoryToProducts.title || title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          // pageTemplate={pageTemplate}
          // elementIndex={elementIndex}
          // position={idx}
          // productsLength={filteredRecommendations.length}
          // rdeType={RDE_TYPE}
        />
      ))}
    </RecoCarousel>
  );
};
